import { c as _c } from "react/compiler-runtime";
import { Box, ButtonBase, Collapse, Stack, Typography } from '@mui/material';
import { type FocusEvent, useEffect, useId, useRef, useState } from 'react';
import { ColorBox } from '@/components/display/ColorBox/ColorBox';
import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ChevronDownIcon } from '@/components/icons/ChevronDownIcon';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { KEEPS_FIELD_FOCUS_ATTRIBUTE } from '@/modules/documents/components/HouseholdDocumentsViewer/ParagraphFind';
import { COLORS } from '@/styles/tokens/colors';
import { AnswerReviewEditor } from './AnswerReviewEditor';
import { AnswerReviewEvidenceCarousel } from './AnswerReviewEvidenceCarousel';
import type { AnswerReviewFieldViewModel, AnswerReviewWorkbenchProps } from './AnswerReviewWorkbench.types';
interface AnswerReviewFieldRowProps extends Pick<AnswerReviewWorkbenchProps, 'onAnswerChange' | 'onAnswerBlur' | 'onSelectEvidence' | 'selectedEvidence' | 'onFocusField' | 'onCloseField' | 'onCancelCitation' | 'onAddCitation' | 'citationAction'> {
  field: AnswerReviewFieldViewModel;
  depth?: number;
  /** Use the collection's compact status spacing. */
  collection?: boolean;
  readOnly: boolean;
  autoFocus?: boolean;
  isFocused?: boolean;
  /** Reports when focus enters or leaves this row, including its select menu. */
  onEditingChange?: (fieldId: string, isEditing: boolean) => void;
}
export function getStatusPresentation(field: AnswerReviewFieldViewModel) {
  if (field.currentRevision?.origin === 'USER') return {
    label: 'Edited',
    color: COLORS.GRAY[600]
  };
  if (field.currentRevision?.confidence === 'EMPTY') return {
    label: 'Empty',
    color: COLORS.GRAY[400]
  };
  if (field.currentRevision?.confidence === 'HIGH') return {
    label: 'High confidence',
    color: COLORS.TEAL[300]
  };
  return {
    label: 'Needs review',
    color: COLORS.FUNCTIONAL.WARNING[500]
  };
}
export function AnswerReviewFieldRow(t0) {
  const $ = _c(107);
  const {
    field,
    depth: t1,
    collection: t2,
    readOnly,
    autoFocus,
    onAnswerChange,
    onAnswerBlur,
    onSelectEvidence,
    selectedEvidence,
    onFocusField,
    onCloseField,
    onCancelCitation,
    onAddCitation,
    citationAction,
    isFocused: t3,
    onEditingChange
  } = t0;
  const depth = t1 === undefined ? 0 : t1;
  const collection = t2 === undefined ? false : t2;
  const isFocused = t3 === undefined ? false : t3;
  const rowRef = useRef(null);
  const isMenuOpenRef = useRef(false);
  const lastEvidenceId = field.evidence.at(-1)?.id;
  const hasAddedCitation = field.evidence.at(-1)?.origin === "USER";
  const [isExpanded, setIsExpanded] = useState(!!hasAddedCitation);
  const [citationIndex, setCitationIndex] = useState(hasAddedCitation ? field.evidence.length - 1 : 0);
  let t4;
  if ($[0] !== field.evidence || $[1] !== selectedEvidence?.id || $[2] !== selectedEvidence?.paragraphIds) {
    let t5;
    if ($[4] !== selectedEvidence?.id || $[5] !== selectedEvidence?.paragraphIds) {
      t5 = evidence => evidence.id === selectedEvidence?.id || evidence.paragraphIds?.some(id => selectedEvidence?.paragraphIds?.includes(id));
      $[4] = selectedEvidence?.id;
      $[5] = selectedEvidence?.paragraphIds;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    t4 = field.evidence.findIndex(t5);
    $[0] = field.evidence;
    $[1] = selectedEvidence?.id;
    $[2] = selectedEvidence?.paragraphIds;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const focusedCitationIndex = Math.max(0, t4);
  let t5;
  if ($[7] !== field.evidence.length || $[8] !== hasAddedCitation) {
    t5 = () => {
      if (hasAddedCitation) {
        setCitationIndex(field.evidence.length - 1);
      } else {
        setCitationIndex(index => Math.min(index, Math.max(0, field.evidence.length - 1)));
      }
    };
    $[7] = field.evidence.length;
    $[8] = hasAddedCitation;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== field.evidence.length || $[11] !== hasAddedCitation || $[12] !== lastEvidenceId) {
    t6 = [lastEvidenceId, hasAddedCitation, field.evidence.length];
    $[10] = field.evidence.length;
    $[11] = hasAddedCitation;
    $[12] = lastEvidenceId;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  useEffect(t5, t6);
  const detailsId = useId();
  let t7;
  if ($[14] !== field) {
    t7 = getStatusPresentation(field);
    $[14] = field;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const status = t7;
  const hasDetails = !!onAddCitation || !!field.reasoning || field.evidence.length > 0;
  const firstEvidence = field.evidence[0];
  const supportingSummary = field.reasoning || firstEvidence?.quote;
  let citationBackground = COLORS.GRAY[50];
  let citationInstruction = `${field.evidence.length} ${field.evidence.length === 1 ? "reference" : "references"} found`;
  if (citationAction === "add") {
    citationBackground = COLORS.TEAL[50];
    citationInstruction = "Select a paragraph in the document to add a reference";
  }
  if (citationAction === "remove") {
    citationBackground = COLORS.FUNCTIONAL.ERROR[50];
    citationInstruction = "Select a highlighted paragraph to remove its reference";
  }
  let t8;
  if ($[16] !== citationAction || $[17] !== field.id || $[18] !== isFocused || $[19] !== onCloseField || $[20] !== onEditingChange) {
    t8 = event => {
      const nextFocus = event.relatedTarget;
      const isFocusInRow = nextFocus instanceof Node && !!rowRef.current?.contains(nextFocus);
      if (isMenuOpenRef.current || isFocusInRow) {
        return;
      }
      onEditingChange?.(field.id, false);
      const keepsFocus = nextFocus instanceof Element && !!nextFocus.closest(`[${KEEPS_FIELD_FOCUS_ATTRIBUTE}]`);
      if (isFocused && !citationAction && !keepsFocus) {
        onCloseField?.();
      }
    };
    $[16] = citationAction;
    $[17] = field.id;
    $[18] = isFocused;
    $[19] = onCloseField;
    $[20] = onEditingChange;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleRowBlur = t8;
  let t9;
  if ($[22] !== isExpanded) {
    t9 = isExpanded ? <ChevronDownIcon size={16} color={COLORS.TEXT.SECONDARY} /> : <ChevronRightIcon size={16} color={COLORS.TEXT.SECONDARY} />;
    $[22] = isExpanded;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const expansionIcon = t9;
  const t10 = `answer-review-label-${field.id}`;
  const t11 = `1px solid ${isFocused ? COLORS.GRAY[400] : "transparent"}`;
  const t12 = `1px solid ${isFocused ? COLORS.GRAY[400] : COLORS.GRAY[200]}`;
  const t13 = isFocused ? 1 : 0;
  const t14 = `answer-review-field-${field.id}`;
  const t15 = onFocusField ? 0 : undefined;
  let t16;
  if ($[24] !== field.id || $[25] !== isFocused || $[26] !== onEditingChange || $[27] !== onFocusField) {
    t16 = () => {
      onEditingChange?.(field.id, true);
      if (!isFocused) {
        onFocusField?.(field.id);
      }
    };
    $[24] = field.id;
    $[25] = isFocused;
    $[26] = onEditingChange;
    $[27] = onFocusField;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== field.id || $[30] !== isFocused || $[31] !== onFocusField) {
    t17 = () => {
      if (!isFocused) {
        onFocusField?.(field.id);
      }
    };
    $[29] = field.id;
    $[30] = isFocused;
    $[31] = onFocusField;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      xs: "1fr",
      md: "34% minmax(0, 1fr)"
    };
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== collection || $[35] !== depth) {
    t19 = !collection && depth > 0 ? {
      pl: 4
    } : undefined;
    $[34] = collection;
    $[35] = depth;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const t20 = `answer-review-label-${field.id}`;
  let t21;
  if ($[37] !== field.label || $[38] !== t20) {
    t21 = <Typography id={t20} component="h3" variant="body-xs" color="text.primary" fontWeight={500}>{field.label}</Typography>;
    $[37] = field.label;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t19 || $[41] !== t21) {
    t22 = <Stack direction="row" alignItems="center" gap={1} px={1.5} py={1} sx={t19}>{t21}</Stack>;
    $[40] = t19;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  const t23 = collection ? 0.75 : 0.5;
  const t24 = autoFocus && !readOnly;
  let t25;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = isOpen => {
      isMenuOpenRef.current = isOpen;
    };
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== field || $[45] !== onAnswerBlur || $[46] !== onAnswerChange || $[47] !== readOnly || $[48] !== t24) {
    t26 = <AnswerReviewEditor field={field} readOnly={readOnly} autoFocus={t24} onMenuOpenChange={t25} onAnswerChange={onAnswerChange} onAnswerBlur={onAnswerBlur} />;
    $[44] = field;
    $[45] = onAnswerBlur;
    $[46] = onAnswerChange;
    $[47] = readOnly;
    $[48] = t24;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== collection || $[51] !== detailsId || $[52] !== expansionIcon || $[53] !== hasDetails || $[54] !== isExpanded || $[55] !== status.color || $[56] !== status.label || $[57] !== supportingSummary) {
    t27 = <Stack direction="row" alignItems="center" gap={1} minWidth={0}>{hasDetails ? <ButtonBase type="button" aria-expanded={isExpanded} aria-controls={detailsId} onClick={() => setIsExpanded(_temp)} sx={{
        alignSelf: "stretch",
        justifyContent: "flex-start",
        borderRadius: 1,
        minWidth: 0,
        flex: 1,
        gap: 1,
        py: 0.5,
        textAlign: "left"
      }}><Box flexShrink={0}><ColorBox color={status.color} /></Box><Typography variant="body-xs" sx={{
          whiteSpace: "nowrap",
          color: COLORS.TEXT.PRIMARY
        }}>{status.label}</Typography>{expansionIcon}{supportingSummary && !isExpanded && <Typography variant="body-xs" noWrap={!collection} sx={{
          minWidth: 0,
          color: COLORS.TEXT.SECONDARY,
          lineHeight: "16px"
        }}>{supportingSummary}</Typography>}</ButtonBase> : <Stack direction="row" alignItems="center" gap={1} py={0.5}><ColorBox color={status.color} /><Typography variant="body-xs" sx={{
          color: COLORS.TEXT.PRIMARY
        }}>{status.label}</Typography></Stack>}</Stack>;
    $[50] = collection;
    $[51] = detailsId;
    $[52] = expansionIcon;
    $[53] = hasDetails;
    $[54] = isExpanded;
    $[55] = status.color;
    $[56] = status.label;
    $[57] = supportingSummary;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== field.reasoning) {
    t28 = field.reasoning && <Box><Typography component="p" variant="body-xs" aria-label="AI reasoning" sx={{
        color: COLORS.TEXT.SECONDARY,
        lineHeight: "16px"
      }}>{field.reasoning}</Typography></Box>;
    $[59] = field.reasoning;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  const t29 = isFocused ? focusedCitationIndex : citationIndex;
  let t30;
  if ($[61] !== field.evidence || $[62] !== field.id || $[63] !== field.label || $[64] !== onSelectEvidence || $[65] !== t29) {
    t30 = <AnswerReviewEvidenceCarousel fieldId={field.id} fieldLabel={field.label} evidence={field.evidence} citationIndex={t29} onSelectEvidence={onSelectEvidence} />;
    $[61] = field.evidence;
    $[62] = field.id;
    $[63] = field.label;
    $[64] = onSelectEvidence;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== detailsId || $[68] !== t28 || $[69] !== t30) {
    t31 = <Stack id={detailsId} gap={0.5}>{t28}{t30}</Stack>;
    $[67] = detailsId;
    $[68] = t28;
    $[69] = t30;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== isExpanded || $[72] !== t31) {
    t32 = <Collapse in={isExpanded} unmountOnExit={true}>{t31}</Collapse>;
    $[71] = isExpanded;
    $[72] = t31;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  let t33;
  if ($[74] !== t23 || $[75] !== t26 || $[76] !== t27 || $[77] !== t32) {
    t33 = <Stack px={1.5} py={1} gap={t23} minWidth={0}>{t26}{t27}{t32}</Stack>;
    $[74] = t23;
    $[75] = t26;
    $[76] = t27;
    $[77] = t32;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] !== t22 || $[80] !== t33) {
    t34 = <Box display="grid" gridTemplateColumns={t18}>{t22}{t33}</Box>;
    $[79] = t22;
    $[80] = t33;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== citationAction || $[83] !== citationBackground || $[84] !== citationInstruction || $[85] !== field.evidence || $[86] !== field.id || $[87] !== field.label || $[88] !== focusedCitationIndex || $[89] !== isFocused || $[90] !== onAddCitation || $[91] !== onCancelCitation || $[92] !== onSelectEvidence || $[93] !== readOnly) {
    t35 = isFocused && <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} px={1.5} py={1} borderTop={`1px solid ${COLORS.GRAY[200]}`} bgcolor={citationBackground} sx={_temp2}>{citationAction !== "add" && field.evidence.length !== 1 && <><IconButton size="xs" variant="transparent" icon={ChevronLeftIcon} ariaLabel={`Previous reference for ${field.label}`} disabled={focusedCitationIndex === 0 || field.evidence.length === 0} onClick={() => {
          const index_0 = focusedCitationIndex - 1;
          const evidence_0 = field.evidence[index_0];
          if (!evidence_0) {
            return;
          }
          if (index_0 === 0) {
            rowRef.current?.focus({
              preventScroll: true
            });
          }
          setCitationIndex(index_0);
          onSelectEvidence?.(field.id, evidence_0);
        }} /><Typography variant="body-xs">{field.evidence.length ? focusedCitationIndex + 1 : 0} of{" "}{field.evidence.length}</Typography><IconButton size="xs" variant="transparent" icon={ChevronRightIcon} ariaLabel={`Next reference for ${field.label}`} disabled={focusedCitationIndex >= field.evidence.length - 1} onClick={() => {
          const index_1 = focusedCitationIndex + 1;
          const evidence_1 = field.evidence[index_1];
          if (!evidence_1) {
            return;
          }
          if (index_1 === field.evidence.length - 1) {
            rowRef.current?.focus({
              preventScroll: true
            });
          }
          setCitationIndex(index_1);
          onSelectEvidence?.(field.id, evidence_1);
        }} /></>}<Typography variant="body-xs" flex={1} sx={{
        color: COLORS.TEXT.PRIMARY
      }}>{citationInstruction}</Typography>{!readOnly && <><Button size="xs" variant="secondary" startIcon={PlusIcon} disabled={!onAddCitation || !!citationAction} onClick={() => onAddCitation?.(field.id, "add")}>Add reference</Button><Button size="xs" variant="secondary" startIcon={Trash01Icon} disabled={!onAddCitation || !!citationAction || field.evidence.length === 0} onClick={() => onAddCitation?.(field.id, "remove")}>Remove reference</Button></>}{citationAction && <Button size="xs" variant="secondary" onClick={() => {
        rowRef.current?.focus({
          preventScroll: true
        });
        onCancelCitation?.();
      }}>Cancel</Button>}</Stack>;
    $[82] = citationAction;
    $[83] = citationBackground;
    $[84] = citationInstruction;
    $[85] = field.evidence;
    $[86] = field.id;
    $[87] = field.label;
    $[88] = focusedCitationIndex;
    $[89] = isFocused;
    $[90] = onAddCitation;
    $[91] = onCancelCitation;
    $[92] = onSelectEvidence;
    $[93] = readOnly;
    $[94] = t35;
  } else {
    t35 = $[94];
  }
  let t36;
  if ($[95] !== handleRowBlur || $[96] !== t10 || $[97] !== t11 || $[98] !== t12 || $[99] !== t13 || $[100] !== t14 || $[101] !== t15 || $[102] !== t16 || $[103] !== t17 || $[104] !== t34 || $[105] !== t35) {
    t36 = <Box component="article" aria-labelledby={t10} border={t11} borderBottom={t12} borderRadius={t13} data-testid={t14} ref={rowRef} tabIndex={t15} onFocus={t16} onClick={t17} onBlur={handleRowBlur}>{t34}{t35}</Box>;
    $[95] = handleRowBlur;
    $[96] = t10;
    $[97] = t11;
    $[98] = t12;
    $[99] = t13;
    $[100] = t14;
    $[101] = t15;
    $[102] = t16;
    $[103] = t17;
    $[104] = t34;
    $[105] = t35;
    $[106] = t36;
  } else {
    t36 = $[106];
  }
  return t36;
}
function _temp2(theme) {
  return {
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius
  };
}
function _temp(expanded) {
  return !expanded;
}